import { createFileRoute } from '@tanstack/react-router';
import { useUser } from '@clerk/react';
import { useQuery } from 'convex/react';
import { TrustLayout } from '../../components/trust/TrustLayout';
import { api } from '../../../convex/_generated/api';

export const Route = createFileRoute('/trust/audit')({
  component: AuditPage,
});

interface AuditRow {
  _id: string;
  op?: string;
  itemId?: string;
  driveId?: string;
  siteId?: string;
  documentId?: string;
  note?: string;
  timestamp?: string;
  event?: string;
  detail?: string;
  ts?: string;
}

function AuditPage() {
  const { isSignedIn } = useUser();

  const writes = useQuery(api.sharepointAudit.getForUser, { limit: 50 }) as
    | AuditRow[]
    | undefined;

  const sandboxRuns = useQuery(api.sharepointSandboxAudit.getForUser, {
    limit: 50,
  }) as AuditRow[] | undefined;

  return (
    <TrustLayout>
      <h1 className="mt-0 text-[22px] text-black">Audit log</h1>
      {!isSignedIn && (
        <p className="text-black leading-relaxed">
          Sign in to view the audit log of every Graph write Repancy has made on your behalf.
        </p>
      )}
      {isSignedIn && (
        <>
          <AuditSection
            title="SharePoint writes"
            description="Every Microsoft Graph write Repancy performs on your behalf is recorded here with the timestamp, operation type, and the SharePoint item it touched."
            rows={writes}
            empty="No audit entries yet. They appear after the first SharePoint import or save-back."
            testIdPrefix="trust-audit-writes"
          />
          <AuditSection
            title="Sandbox runs"
            description="One row for every /trust/sandbox Run click. Detail strings are redacted server-side — no raw UPNs, item IDs, or tokens leave the tenant."
            rows={sandboxRuns}
            empty="No sandbox runs yet. Try the panels at /trust/sandbox."
            testIdPrefix="trust-audit-sandbox"
          />
        </>
      )}
    </TrustLayout>
  );
}

function AuditSection({
  title,
  description,
  rows,
  empty,
  testIdPrefix,
}: {
  title: string;
  description: string;
  rows: AuditRow[] | undefined;
  empty: string;
  testIdPrefix: string;
}) {
  return (
    <section className="mt-6">
      <h2 className="m-0 mb-2 text-[16px] font-semibold text-black">{title}</h2>
      <p className="max-w-[720px] text-black leading-relaxed">{description}</p>

      {rows === undefined && <p className="my-3 italic text-black">Loading…</p>}
      {rows && rows.length === 0 && (
        <p className="my-3 italic text-black">{empty}</p>
      )}
      {rows && rows.length > 0 && (
        <div
          data-testid={`${testIdPrefix}-table`}
          className="overflow-hidden rounded border border-black/10 bg-white"
        >
          <div className="flex gap-3 bg-[var(--xp-selection)] px-3.5 py-2 text-xs font-bold text-white">
            <div className="w-[110px] shrink-0">When</div>
            <div className="w-[160px] shrink-0">Event</div>
            <div className="flex-1">Detail</div>
          </div>
          {rows.map((r) => {
            const when = r.ts ?? r.timestamp ?? '';
            const event = r.event ?? r.op ?? '(unknown)';
            const detail =
              r.detail ??
              [
                r.note,
                r.itemId && `item ${r.itemId}`,
                r.siteId && `site ${r.siteId}`,
              ]
                .filter(Boolean)
                .join(' · ');
            return (
              <div
                key={r._id}
                data-testid="trust-audit-row"
                className="flex gap-3 border-t border-black/[0.08] px-3.5 py-1.5 text-xs text-black"
              >
                <div className="w-[110px] shrink-0 font-mono text-[11px]">
                  {when ? new Date(when).toLocaleString() : '—'}
                </div>
                <div className="w-[160px] shrink-0">
                  <EventBadge event={event} />
                </div>
                <div className="flex-1 text-black">{detail || '—'}</div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function EventBadge({ event }: { event: string }) {
  const lower = event.toLowerCase();
  let bg = 'bg-[var(--xp-selection)]';
  if (lower.startsWith('sandbox-')) bg = 'bg-[#660066]';
  else if (lower.includes('conflict') || lower.includes('error')) bg = 'bg-[#cc0000]';
  else if (lower.includes('save') || lower.includes('import')) bg = 'bg-[#008000]';
  else if (lower.includes('delta') || lower.includes('probe')) bg = 'bg-[#FF8C00]';
  return (
    <span
      data-testid="trust-audit-badge"
      className={`inline-block rounded-sm ${bg} px-2 py-px text-[10px] font-bold text-white`}
    >
      {event}
    </span>
  );
}
